import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { VotemateLogo } from '../votemate-logo/VotemateLogo';
import { ChatInput } from './ChatComponents';
import type { AnswerDepth } from './AnswerDepth';
import type { SendChatOptions } from '@/contexts/ChatContext';
import OutlinedButton from '../../ui-library/OutlinedButton';
import { primitiveColors, semanticColors, semanticGradients } from '@/theme/tokens';
import { ContextService, ConversationContext } from '@/services/contextService';
import useSmartPrompts from '@/hooks/useSmartPrompts';
import useAnalytics from '@/hooks/useAnalytics';
import { useNavMode } from '@/hooks/useNavMode';
import { isolate } from '@/utils/bidi';
import { useDirection } from '@/hooks/useDirection';
import useBallotLoading from '@/hooks/useBallotLoading';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SuggestedPrompt {
  label: string;
  prompt: string;
  category: 'general' | 'race' | 'measure' | 'transparency';
  /** Template id, reported as the analytics label for a tap. */
  id: string;
  /**
   * A ranked backend template, or one of the local sets below. Keeping both in
   * one vocabulary lets the funnel compare ranked chips against the static
   * ones they replaced.
   */
  source: 'catalog' | 'fallback';
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getTimeOfDayGreetingKey():
  'landing.greeting.morning' | 'landing.greeting.afternoon' | 'landing.greeting.evening' {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'landing.greeting.morning';
  if (hour >= 12 && hour < 17) return 'landing.greeting.afternoon';
  return 'landing.greeting.evening';
}

function daysUntil(dateStr: string): number | null {
  try {
    const target = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffMs = target.getTime() - today.getTime();
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return days >= 0 ? days : null;
  } catch {
    return null;
  }
}

function buildDynamicChips(
  ctx: ConversationContext,
  t: TFunction,
  locale: string,
  dir: 'ltr' | 'rtl'
): SuggestedPrompt[] | null {
  const election = ctx.election?.elections?.[0];
  if (!election) return null;

  const chips: SuggestedPrompt[] = [];

  // ── Chip 1: time-sensitive election countdown ──
  const days = daysUntil(election.election_date);
  if (days !== null) {
    const daysText =
      days === 0
        ? t('landing.prompts.dynamic.daysText.today')
        : days === 1
          ? t('landing.prompts.dynamic.daysText.tomorrow')
          : t('landing.prompts.dynamic.daysText.inDays', { count: days, days });
    const prettyDate = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(election.election_date));
    // U+2068/U+2069 (FSI/PDI) isolate the LTR election name inside RTL prose.
    const electionName = isolate(election.election_name);
    chips.push({
      label: t('landing.prompts.dynamic.electionCountdown.label', {
        electionName,
        daysText,
      }),
      prompt: t('landing.prompts.dynamic.electionCountdown.prompt', {
        electionName,
        prettyDate,
      }),
      category: 'general',
      id: 'fallback_election_countdown',
      source: 'fallback',
    });
  }

  // ── Chip 2: "Tell me about [first local/state/federal race]" ──
  // Uses the `level` field from Democracy Works data. Priority: local → state → federal.
  const contests = election.contests || [];
  const raceChip =
    contests.find((c) => c.level?.toLowerCase() === 'local') ||
    contests.find((c) => c.level?.toLowerCase() === 'state') ||
    contests.find((c) => c.level?.toLowerCase() === 'federal') ||
    contests[0];
  if (raceChip) {
    // Strip "— elect one" suffix: "Mayor of Los Angeles — elect one" → "Mayor of Los Angeles"
    const rawTitle = raceChip.title.replace(/\s*[—–-]\s*elect\s+one.*$/i, '');
    const displayTitle = dir === 'rtl' ? isolate(rawTitle) : rawTitle;
    chips.push({
      label: t('landing.prompts.dynamic.raceInfo.label', { displayTitle }),
      prompt: t('landing.prompts.dynamic.raceInfo.prompt', { displayTitle }),
      category: 'race',
      id: 'fallback_race_info',
      source: 'fallback',
    });
  }

  // ── Chip 3: early voting ──
  chips.push({
    label: t('landing.prompts.dynamic.earlyVoting.label'),
    prompt: t('landing.prompts.dynamic.earlyVoting.prompt'),
    category: 'general',
    id: 'fallback_early_voting',
    source: 'fallback',
  });

  // Only return if we built at least 2 valid chips
  return chips.length >= 2 ? chips : null;
}

function getDefaultPrompts(t: TFunction): SuggestedPrompt[] {
  return [
    {
      label: t('landing.prompts.default.nextElection.label'),
      prompt: t('landing.prompts.default.nextElection.prompt'),
      category: 'general',
      id: 'fallback_next_election',
      source: 'fallback',
    },
    {
      label: t('landing.prompts.default.earlyVoting.label'),
      prompt: t('landing.prompts.default.earlyVoting.prompt'),
      category: 'general',
      id: 'fallback_early_voting',
      source: 'fallback',
    },
    {
      label: t('landing.prompts.default.myReps.label'),
      prompt: t('landing.prompts.default.myReps.prompt'),
      category: 'general',
      id: 'fallback_my_reps',
      source: 'fallback',
    },
  ];
}

function getReturningUserPrompts(t: TFunction): SuggestedPrompt[] {
  return [
    {
      label: t('landing.prompts.returning.newOnBallot.label'),
      prompt: t('landing.prompts.returning.newOnBallot.prompt'),
      category: 'general',
      id: 'fallback_new_on_ballot',
      source: 'fallback',
    },
    {
      label: t('landing.prompts.returning.nextElection.label'),
      prompt: t('landing.prompts.returning.nextElection.prompt'),
      category: 'general',
      id: 'fallback_next_election',
      source: 'fallback',
    },
    {
      label: t('landing.prompts.returning.researchCandidate.label'),
      prompt: t('landing.prompts.returning.researchCandidate.prompt'),
      category: 'race',
      id: 'fallback_research_candidate',
      source: 'fallback',
    },
  ];
}

// ─── Component ────────────────────────────────────────────────────────────────

interface ChatLandingStateProps {
  onSend: (value: string, options?: SendChatOptions) => void;
  onSuggestionClick: (suggestion: string, promptId?: string) => void;
  disabled?: boolean;
  isBallotOpen?: boolean;
  userName?: string;
  isReturningUser?: boolean;
  /** Answer depth for this session, threaded down to the input's selector. */
  depth?: AnswerDepth;
  onDepthChange?: (depth: AnswerDepth) => void;
  /** Live sheet height in px (mobile-web bottom sheet). The block rides up by
   * half of it so it stays visible above the open sheet; 0 leaves it centered. */
  sheetLiftPx?: number;
  /** True while the sheet is being dragged — suppresses the snap transition so
   * the block tracks the finger instead of easing behind it. */
  sheetDragging?: boolean;
}

export const ChatLandingState = React.memo<ChatLandingStateProps>(function ChatLandingState({
  onSend,
  onSuggestionClick,
  disabled = false,
  isBallotOpen = false,
  userName,
  isReturningUser = false,
  depth,
  onDepthChange,
  sheetLiftPx = 0,
  sheetDragging = false,
}) {
  const { t, i18n } = useTranslation('chat');
  const navMode = useNavMode();
  const locale = i18n.language;
  const analytics = useAnalytics();
  const dir = useDirection();

  // isReturningUser = true when user is logged in (passed from ChatContainer)
  const isAuthenticated = isReturningUser;
  const ballotLoading = useBallotLoading();

  // Session context, held for both audiences. The initializer catches the case
  // where it was already cached (login, or a guest address saved, before this
  // component mounted); the subscription carries later re-initializations.
  const [conversationContext, setConversationContext] = useState<ConversationContext | null>(() =>
    ContextService.getContext()
  );
  useEffect(() => ContextService.subscribe(setConversationContext), []);

  // The local fallback chips stay signed-in only, as before.
  const dynamicChips = useMemo(
    () =>
      isAuthenticated && conversationContext
        ? buildDynamicChips(conversationContext, t, locale, dir)
        : null,
    [isAuthenticated, conversationContext, t, locale, dir]
  );

  // A guest's signals live in this browser, so hand the ranking the context
  // this client already builds for chat — without it a guest who has saved an
  // address still gets the generic onboarding set. A signed-in voter's records
  // are authoritative and are read server-side instead.
  const clientContext = isAuthenticated ? undefined : (conversationContext ?? undefined);

  // Ranked prompts from the backend, which reads this voter's ballot,
  // conversation history, and distilled priorities. `null` means "nothing
  // better than the default" — a failed fetch, one still in flight, or a
  // response whose every template this build does not ship — so the local
  // chip sets below stay the safety net.
  const { prompts: smartPrompts, isLoadingFirstResult: isLoadingSmartPrompts } = useSmartPrompts({
    clientContext,
  });

  const prompts = useMemo(() => {
    if (smartPrompts) return smartPrompts;
    if (!isAuthenticated) return getDefaultPrompts(t);
    return dynamicChips || getReturningUserPrompts(t);
  }, [smartPrompts, isAuthenticated, dynamicChips, t]);

  const handleSuggestionClick = useCallback(
    (suggestion: SuggestedPrompt, rank: number) => {
      // `source` distinguishes a ranked chip from one of the local fallback
      // sets, so the funnel can compare the two rather than only counting taps.
      analytics.track('Suggested Prompt Tapped', {
        promptId: suggestion.id,
        source: suggestion.source,
        rank,
        category: suggestion.category,
        surface: 'landing',
      });
      onSuggestionClick(suggestion.prompt, suggestion.id);
    },
    [analytics, onSuggestionClick]
  );

  const greetingKey = getTimeOfDayGreetingKey();
  const greeting = t(greetingKey, {
    context: userName ? 'withName' : undefined,
    userName: dir === 'rtl' && userName ? isolate(userName) : userName,
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        pl: { xs: 2, sm: 4, md: isBallotOpen ? 'calc(32px + 35px)' : 4 },
        pr: { xs: 2, sm: 4 },
        // Optical centering (block sits 24px low, not symmetric): it's top-heavy
        // (logo + composer, airy chips below). Bottom padding clears the
        // keyboard/safe-area, plus the tab bar in tab mode.
        pt: { xs: 9, sm: 8 },
        pb: {
          xs:
            navMode === 'tab'
              ? 'calc(24px + max(var(--keyboard-inset-px, 0px), env(safe-area-inset-bottom)) + var(--mobile-tab-bar-height, 0px))'
              : 'calc(24px + max(var(--keyboard-inset-px, 0px), env(safe-area-inset-bottom)))',
          sm: 8,
        },
        gap: 3,
        // Ride up above the open bottom sheet (mobile-web); centered when closed.
        transform: sheetLiftPx ? `translateY(${-sheetLiftPx / 2}px)` : 'none',
        transition: sheetDragging ? 'none' : 'transform 0.25s ease',
        willChange: 'transform',
      }}
    >
      {isAuthenticated ? (
        <>
          {/* Returning user — minimal: logo inline with greeting */}
          <Box
            aria-live="polite"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1.5, sm: 2 },
              maxWidth: '100%',
            }}
          >
            <VotemateLogo size={32} logoOnly />
            <Typography
              variant="h3"
              component="h1"
              sx={{
                color: semanticColors.textPrimary,
                fontWeight: 400,
                fontSize: { xs: '24px', sm: '32px', md: '38px' },
                // The saved first name can be up to 100 chars — wrap (mobile
                // app parity) instead of forcing horizontal overflow.
                minWidth: 0,
                overflowWrap: 'anywhere',
              }}
            >
              {greeting}
            </Typography>
          </Box>
        </>
      ) : (
        /* First-time welcome — full branding */
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography component="h1" sx={visuallyHidden}>
            {t('landing.heading')}
          </Typography>
          <VotemateLogo size={40} />
        </Box>
      )}

      {/* Centered prompt input */}
      <Box sx={{ width: '100%', maxWidth: '600px' }}>
        <ChatInput
          onSend={onSend}
          disabled={disabled}
          showHelper={false}
          depth={depth}
          onDepthChange={onDepthChange}
          voiceEntryPoint="landing"
        />
      </Box>

      {/* Suggested actions */}
      <Stack
        direction="row"
        flexWrap="wrap"
        justifyContent="center"
        useFlexGap
        role="group"
        sx={{
          width: '100%',
          maxWidth: '600px',
          '&.MuiStack-root': { gap: { xs: 1, sm: 1.5 } },
        }}
        aria-label={t('landing.suggestedActionsLabel')}
      >
        {(isLoadingSmartPrompts ? [] : prompts).map((p, rank) => (
          <OutlinedButton
            key={`${p.id}:${rank}`}
            onClick={() => handleSuggestionClick(p, rank)}
            gradientOutline
            disabled={disabled || ballotLoading}
            styleOverrides={{
              // ``OutlinedButton`` ships with:
              //   - root: ``background: theme.tokens.colors.primary``
              //     (which is a SOLID blue1 — misnamed "gradient")
              //     and ``padding: 0.2px``
              //   - inner: ``border: 2px solid transparent`` with
              //     ``backgroundClip: padding-box``
              // The 2px transparent inner border is what controls the
              // VISIBLE frame thickness — the gradient from the root
              // paints through that 2px window. Override both: real
              // gradient on the root + 1px inner border for a thinner
              // visible frame. The root ``padding: 0`` removes the
              // extra spacing it added.
              root: {
                background: semanticGradients.primary,
                padding: 0,
              },
              outlinedButton: {
                fontSize: 14,
                paddingTop: '6px',
                paddingBottom: '6px',
                paddingLeft: '16px',
                paddingRight: '16px',
                fontWeight: 500,
                whiteSpace: { xs: 'normal', sm: 'nowrap' },
                textAlign: 'center',
                maxWidth: { xs: '90vw', sm: 'none' },
                border: '1.5px solid transparent',
                // Tinted blue fill (mirrors main's chip styling) +
                // dark-teal-ish text for readability over the tint.
                // ``backgroundColor`` (not ``background``) preserves
                // the inherited ``backgroundClip: padding-box`` so
                // the gradient border shows through the transparent
                // border in both rest and hover states.
                backgroundColor: semanticColors.chatBotMessage,
                color: primitiveColors.rockSlate,
                '&:hover': {
                  backgroundColor: semanticColors.chatBotMessageHover,
                  color: primitiveColors.rockSlate,
                  borderColor: 'transparent',
                },
              },
            }}
          >
            {p.label}
          </OutlinedButton>
        ))}
      </Stack>
    </Box>
  );
});
